/**
 * ノーコード（rule_tree）で作ったチェックリストの抽出条件表示。
 *
 * **文言はBEが組み立てて返す。** フロントは V1(Vue2) と V2(Next.js) の2つあり、
 * 文言を両方に持つと片方だけ直す事故が起きるため。ここは「出すか出さないか」と
 * 「並べ方」だけを持つ。
 *
 * `extraction_conditions` が無い（＝ルールツリー未設定の）チェックリストでは
 * 従来の law_ids / regulation_ids / dbt_types ベースの表示に落とすこと。
 */

export interface ExtractionConditionRow {
  label: string;
  /** 値は畳まずに全部出す */
  values: string[];
  /**
   * 演算子の語。**ロケールによって置く側が変わる**ので前後2つある。
   * 日本語は「以上」「超」など後置詞なので `suffix` 側。英語は
   * `greater than` / `other than` が前置で、後置にすると意味が逆に読める。
   * 片方にしか入らない（`=` と `in` はどちらも入らない）。
   */
  prefix?: string;
  suffix?: string;
}

export interface ExtractionConditionGroup {
  /** 複数グループのときだけ入る（①②…） */
  marker?: string;
  title: string;
  rows: ExtractionConditionRow[];
  /** 物質レベルの補足。SDSレベルの除外は excludes 側に出る */
  notes: string[];
  /**
   * 入れ子の and/or。対象ごとにサブグループが入る。
   *
   * 平坦化して条件行を親へ引き上げると、法令ごとに閾値が違う設定で
   * 「どの法令がどの閾値か」が読めなくなる。深さは打ち切らず再帰で描く。
   */
  children?: ExtractionConditionGroup[];
}

export interface ExtractionConditionExcludes {
  heading: string;
  /** 行ラベルと注記もBEが返す。FEに文言を持たせない（V1・V2で食い違うため） */
  row_label?: string;
  note?: string;
  items: ExtractionConditionGroup[];
}

export interface ExtractionConditions {
  /** グループが1つだけのときは null（見出しも番号も出さず現行の見え方を保つ） */
  heading?: string | null;
  groups: ExtractionConditionGroup[];
  excludes?: ExtractionConditionExcludes | null;
}

/**
 * ルールツリー由来の抽出条件を出すか。
 *
 * 空の器で true を返すと、見出しだけの空欄が顧客画面に出る。
 */
export const hasRuleTreeConditions = (
  conditions?: ExtractionConditions | null
): boolean => {
  if (!conditions) return false;
  if (conditions.groups && conditions.groups.length > 0) return true;
  return !!(
    conditions.excludes &&
    conditions.excludes.items &&
    conditions.excludes.items.length > 0
  );
};

/**
 * 1行分の値を表示用の文字列にする。
 *
 * 値が無ければ空文字にして、「含有率の条件: 以上」のような
 * 意味の通らない行を作らない。
 */
export const formatRowValues = (row: ExtractionConditionRow): string => {
  const values = row.values || [];
  if (values.length === 0) return '';

  const parts = [];
  if (row.prefix) parts.push(row.prefix);
  parts.push(values.join('・'));
  if (row.suffix) parts.push(row.suffix);
  return parts.join(' ');
};
